import * as React from 'react';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import {
    Box,
    Button,
    Grid,
    LinearProgress,
    Stack,
    TextField,
    Typography,
    Paper,
    Chip,
    IconButton,
    Collapse,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Card,
    CardContent,
    Divider,
    Pagination,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Refresh as RefreshIcon,
    Email as EmailIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';

const GET_VERIFY_ROLES = gql`
    query ($eventId: String!) {
        getverifyroles(eventId: $eventId)
    }
`;

const GET_SENDER_EMAIL = gql`
    query ($eventId: String!, $role: ROLE!) {
        getSenderEmail(eventId: $eventId, role: $role)
    }
`;

const GET_GROUPED_EMAIL_TRACKING = gql`
    query GetGroupedTrackingEmail(
        $senderEmail: String!
        $search: String
        $fromDate: String
        $toDate: String
        $event: String
        $eventId: String
        $limit: Int
        $offset: Int
    ) {
        getGroupedTrackingEmail(
            senderEmail: $senderEmail
            search: $search
            fromDate: $fromDate
            toDate: $toDate
            event: $event
            eventId: $eventId
            limit: $limit
            offset: $offset
        ) {
            id
            from_address
            recipient
            subject
            latestEvent
            latestEventTime
            status
            totalEvents
            firstSent
            lastUpdated
            events {
                id
                event
                reason
                url
                created
            }
        }
        getGroupedTrackingEmailCount(
            senderEmail: $senderEmail
            search: $search
            fromDate: $fromDate
            toDate: $toDate
            event: $event
            eventId: $eventId
        )
    }
`;

export default function EmailTracker() {
    const [role, setRole] = React.useState(null);
    const { eventId } = useParams();

    const {
        data: featureData,
        loading: featureLoading,
        error: featureError,
    } = useQuery(GET_VERIFY_ROLES, {
        variables: { eventId },
    });

    const [getSenderEmail, { data: senderEmail, loading: senderLoading, error: senderError }] =
        useLazyQuery(GET_SENDER_EMAIL);

    React.useEffect(() => {
        getSenderEmail({ variables: { eventId, role: 'visitor' } });
    }, [featureData, getSenderEmail, eventId]);

    if (featureLoading || senderLoading) {
        return <LinearProgress />;
    }

    if (featureError) {
        return <Typography>{featureError.message}</Typography>;
    }

    if (senderError) {
        return <Typography>{senderError.message}</Typography>;
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={2}>
                <MainCard>
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <Stack
                                direction='column'
                                justifyContent='space-between'
                                sx={{ bgcolor: 'background.paper', width: '100%', mb: 2 }}
                            >
                                <Typography variant='h1' textAlign='center' marginY={2}>
                                    Email Tracking
                                </Typography>
                                {senderEmail?.getSenderEmail && (
                                    <GroupedEmailTable senderEmail={senderEmail?.getSenderEmail} />
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </MainCard>
            </Stack>
        </LocalizationProvider>
    );
}

function GroupedEmailTable({ senderEmail }) {
    const [search, setSearch] = React.useState('');
    const [debouncedSearch, setDebouncedSearch] = React.useState('');
    const [fromDate, setFromDate] = React.useState(null);
    const [toDate, setToDate] = React.useState(null);
    const [eventFilter, setEventFilter] = React.useState('');
    const [expandedEmails, setExpandedEmails] = React.useState(new Set());
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(20);

    const { eventId } = useParams();

    // Debounce search input
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1); // Reset to first page when search changes
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    // Reset page when filters change
    React.useEffect(() => {
        setPage(1);
    }, [fromDate, toDate, eventFilter, debouncedSearch]);

    const { data, loading, error, refetch } = useQuery(GET_GROUPED_EMAIL_TRACKING, {
        variables: {
            senderEmail,
            search: debouncedSearch || null,
            fromDate: fromDate ? fromDate.toISOString() : null,
            toDate: toDate ? toDate.toISOString() : null,
            event: eventFilter || null,
            eventId: eventId,
            limit: pageSize,
            offset: (page - 1) * pageSize,
        },
        fetchPolicy: 'cache-and-network',
    });

    const toggleExpand = emailId => {
        setExpandedEmails(prev => {
            const newSet = new Set(prev);
            if (newSet.has(emailId)) {
                newSet.delete(emailId);
            } else {
                newSet.add(emailId);
            }
            return newSet;
        });
    };

    const getStatusColor = status => {
        const statusLower = status?.toLowerCase() || '';
        switch (statusLower) {
            case 'delivered':
            case 'deliver':
                return 'success';
            case 'opened':
            case 'open':
                return 'info';
            case 'clicked':
            case 'click':
                return 'primary';
            case 'bounced':
            case 'bounce':
            case 'rejected':
            case 'reject':
                return 'error';
            case 'spam':
            case 'unsubscribed':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getStatusIcon = status => {
        const statusLower = status?.toLowerCase() || '';
        switch (statusLower) {
            case 'delivered':
            case 'deliver':
                return <CheckCircleIcon fontSize='small' />;
            case 'opened':
            case 'open':
                return <EmailIcon fontSize='small' />;
            case 'clicked':
            case 'click':
                return <OpenInNewIcon fontSize='small' />;
            case 'bounced':
            case 'bounce':
            case 'rejected':
            case 'reject':
                return <ErrorIcon fontSize='small' />;
            case 'spam':
            case 'unsubscribed':
                return <WarningIcon fontSize='small' />;
            default:
                return <InfoIcon fontSize='small' />;
        }
    };

    const titleCase = str => {
        if (!str) return '';
        return str
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Helper function to safely format dates (handles timestamps in milliseconds)
    const formatDate = dateValue => {
        if (!dateValue) return 'N/A';
        try {
            // If it's a timestamp string or number, convert to number first
            const timestamp = typeof dateValue === 'string' ? parseInt(dateValue, 10) : dateValue;
            const date = new Date(timestamp);
            // Check if date is valid
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            return date.toLocaleString();
        } catch (error) {
            return 'Invalid Date';
        }
    };

    if (loading && !data) return <LinearProgress />;
    if (error) return <Typography color='error'>{error.message}</Typography>;

    const emails = data?.getGroupedTrackingEmail || [];
    const totalCount = data?.getGroupedTrackingEmailCount || 0;
    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
            {/* Enhanced Filters Section */}
            <Paper
                elevation={2}
                sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                    border: '1px solid rgba(102, 126, 234, 0.1)',
                }}
            >
                <Stack spacing={3}>
                    {/* Filter Controls */}
                    <Box>
                        <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                            🔍 Filter & Search
                        </Typography>
                        <Grid container spacing={2} alignItems='center'>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label='Search'
                                    variant='outlined'
                                    size='small'
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder='Search by subject, recipient, or sender...'
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)',
                                            },
                                            '&.Mui-focused': {
                                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.5}>
                                <DatePicker
                                    label='From Date'
                                    value={fromDate}
                                    onChange={setFromDate}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            sx: {
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                },
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.5}>
                                <DatePicker
                                    label='To Date'
                                    value={toDate}
                                    onChange={setToDate}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            sx: {
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                },
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth size='small'>
                                    <InputLabel>Event Type</InputLabel>
                                    <Select
                                        value={eventFilter}
                                        label='Event Type'
                                        onChange={e => setEventFilter(e.target.value)}
                                        sx={{
                                            borderRadius: 2,
                                        }}
                                    >
                                        <MenuItem value=''>All Events</MenuItem>
                                        <MenuItem value='delivered'>Delivered</MenuItem>
                                        <MenuItem value='opened'>Opened</MenuItem>
                                        <MenuItem value='clicked'>Clicked</MenuItem>
                                        <MenuItem value='bounced'>Bounced</MenuItem>
                                        <MenuItem value='rejected'>Rejected</MenuItem>
                                        <MenuItem value='spam'>Spam</MenuItem>
                                        <MenuItem value='unsubscribed'>Unsubscribed</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <Stack direction='row' spacing={1}>
                                    <Button
                                        variant='contained'
                                        startIcon={<RefreshIcon />}
                                        disabled={loading}
                                        onClick={() => refetch()}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            px: 2,
                                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                            '&:hover': {
                                                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                                                transform: 'translateY(-1px)',
                                            },
                                        }}
                                    >
                                        Refresh
                                    </Button>
                                    {(search || fromDate || toDate || eventFilter) && (
                                        <Button
                                            variant='outlined'
                                            onClick={() => {
                                                setSearch('');
                                                setFromDate(null);
                                                setToDate(null);
                                                setEventFilter('');
                                            }}
                                            sx={{
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Clear
                                        </Button>
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Results Summary & Pagination Controls */}
                    <Divider />
                    <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={2}>
                        <Typography variant='body2' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            📊 Showing <strong style={{ color: 'primary.main' }}>{emails.length}</strong> of{' '}
                            <strong style={{ color: 'primary.main' }}>{totalCount}</strong> unique email(s)
                        </Typography>
                        <FormControl size='small' sx={{ minWidth: 140 }}>
                            <InputLabel>Items Per Page</InputLabel>
                            <Select
                                value={pageSize}
                                label='Items Per Page'
                                onChange={e => {
                                    setPageSize(e.target.value);
                                    setPage(1);
                                }}
                                sx={{
                                    borderRadius: 2,
                                }}
                            >
                                <MenuItem value={10}>10 per page</MenuItem>
                                <MenuItem value={20}>20 per page</MenuItem>
                                <MenuItem value={50}>50 per page</MenuItem>
                                <MenuItem value={100}>100 per page</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </Stack>
            </Paper>

            {/* Email List */}
            <Box>
                {emails.length === 0 ? (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 6,
                            textAlign: 'center',
                            borderRadius: 2,
                            background:
                                'linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%)',
                        }}
                    >
                        <Typography variant='h5' color='text.secondary' sx={{ mb: 1, fontWeight: 600 }}>
                            📭 No emails found
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                            {search || fromDate || toDate || eventFilter
                                ? 'Try adjusting your filters to see more results'
                                : 'No email tracking data available for this sender'}
                        </Typography>
                    </Paper>
                ) : (
                    <Stack spacing={2.5}>
                        {emails.map(email => {
                            const isExpanded = expandedEmails.has(email.id);
                            return (
                                <Card
                                    key={email.id}
                                    elevation={2}
                                    sx={{
                                        borderRadius: 3,
                                        transition: 'all 0.3s ease',
                                        border: '1px solid rgba(102, 126, 234, 0.1)',
                                        '&:hover': {
                                            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)',
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Stack spacing={2.5}>
                                            {/* Header Section */}
                                            <Stack
                                                direction={{ xs: 'column', md: 'row' }}
                                                justifyContent='space-between'
                                                alignItems={{ xs: 'flex-start', md: 'flex-start' }}
                                                spacing={2}
                                            >
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography
                                                        variant='h6'
                                                        sx={{
                                                            fontWeight: 700,
                                                            mb: 1.5,
                                                            wordBreak: 'break-word',
                                                            color: 'text.primary',
                                                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                                                        }}
                                                    >
                                                        {email.subject}
                                                    </Typography>
                                                    <Stack direction='row' spacing={2} flexWrap='wrap' sx={{ mt: 1 }}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                px: 1.5,
                                                                py: 0.5,
                                                                borderRadius: 2,
                                                                bgcolor: 'rgba(102, 126, 234, 0.08)',
                                                            }}
                                                        >
                                                            <Typography
                                                                variant='caption'
                                                                sx={{ fontWeight: 600, color: 'text.secondary' }}
                                                            >
                                                                To:
                                                            </Typography>
                                                            <Typography
                                                                variant='body2'
                                                                sx={{ fontWeight: 500, color: 'text.primary' }}
                                                            >
                                                                {email.recipient}
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                px: 1.5,
                                                                py: 0.5,
                                                                borderRadius: 2,
                                                                bgcolor: 'rgba(118, 75, 162, 0.08)',
                                                            }}
                                                        >
                                                            <Typography
                                                                variant='caption'
                                                                sx={{ fontWeight: 600, color: 'text.secondary' }}
                                                            >
                                                                From:
                                                            </Typography>
                                                            <Typography
                                                                variant='body2'
                                                                sx={{ fontWeight: 500, color: 'text.primary' }}
                                                            >
                                                                {email.from_address}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Box>
                                                <Stack
                                                    direction='row'
                                                    spacing={1.5}
                                                    alignItems='center'
                                                    flexWrap='wrap'
                                                >
                                                    <Chip
                                                        icon={getStatusIcon(email.status)}
                                                        label={titleCase(email.status)}
                                                        color={getStatusColor(email.status)}
                                                        size='medium'
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.875rem',
                                                            height: '32px',
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                        }}
                                                    />
                                                    <Chip
                                                        label={`${email.totalEvents} event${
                                                            email.totalEvents !== 1 ? 's' : ''
                                                        }`}
                                                        size='medium'
                                                        variant='outlined'
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.875rem',
                                                            height: '32px',
                                                        }}
                                                    />
                                                    <IconButton
                                                        size='medium'
                                                        onClick={() => toggleExpand(email.id)}
                                                        sx={{
                                                            bgcolor: 'rgba(102, 126, 234, 0.1)',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(102, 126, 234, 0.2)',
                                                                transform: 'scale(1.1)',
                                                            },
                                                            transition: 'all 0.3s ease',
                                                        }}
                                                    >
                                                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </IconButton>
                                                </Stack>
                                            </Stack>

                                            {/* Summary Timeline */}
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    bgcolor: 'rgba(102, 126, 234, 0.05)',
                                                    border: '1px solid rgba(102, 126, 234, 0.1)',
                                                }}
                                            >
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={4}>
                                                        <Typography
                                                            variant='caption'
                                                            sx={{
                                                                color: 'text.secondary',
                                                                fontWeight: 600,
                                                                display: 'block',
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            📅 Latest Event
                                                        </Typography>
                                                        <Typography
                                                            variant='body2'
                                                            sx={{ color: 'text.primary', fontWeight: 500 }}
                                                        >
                                                            {formatDate(email.latestEventTime)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <Typography
                                                            variant='caption'
                                                            sx={{
                                                                color: 'text.secondary',
                                                                fontWeight: 600,
                                                                display: 'block',
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            ✉️ First Sent
                                                        </Typography>
                                                        <Typography
                                                            variant='body2'
                                                            sx={{ color: 'text.primary', fontWeight: 500 }}
                                                        >
                                                            {formatDate(email.firstSent)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <Typography
                                                            variant='caption'
                                                            sx={{
                                                                color: 'text.secondary',
                                                                fontWeight: 600,
                                                                display: 'block',
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            🔄 Last Updated
                                                        </Typography>
                                                        <Typography
                                                            variant='body2'
                                                            sx={{ color: 'text.primary', fontWeight: 500 }}
                                                        >
                                                            {formatDate(email.lastUpdated)}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Paper>

                                            {/* Event Timeline */}
                                            <Collapse in={isExpanded}>
                                                <Divider sx={{ my: 2 }} />
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography
                                                        variant='subtitle1'
                                                        sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}
                                                    >
                                                        📋 Event Timeline
                                                    </Typography>
                                                    <Chip
                                                        label={`${email.events.length} total event${
                                                            email.events.length !== 1 ? 's' : ''
                                                        }`}
                                                        size='small'
                                                        variant='outlined'
                                                        sx={{ mb: 2 }}
                                                    />
                                                </Box>
                                                <TableContainer
                                                    component={Paper}
                                                    elevation={0}
                                                    sx={{
                                                        borderRadius: 2,
                                                        border: '1px solid rgba(102, 126, 234, 0.1)',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <Table size='small'>
                                                        <TableHead>
                                                            <TableRow
                                                                sx={{
                                                                    bgcolor: 'rgba(102, 126, 234, 0.08)',
                                                                    '& .MuiTableCell-head': {
                                                                        fontWeight: 700,
                                                                        color: 'text.primary',
                                                                        fontSize: '0.875rem',
                                                                    },
                                                                }}
                                                            >
                                                                <TableCell>Event</TableCell>
                                                                <TableCell>Time</TableCell>
                                                                <TableCell>Reason</TableCell>
                                                                <TableCell>URL</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {email.events.map((event, idx) => (
                                                                <TableRow
                                                                    key={event.id || idx}
                                                                    sx={{
                                                                        '&:hover': {
                                                                            bgcolor: 'rgba(102, 126, 234, 0.03)',
                                                                        },
                                                                        '&:last-child td': {
                                                                            borderBottom: 0,
                                                                        },
                                                                    }}
                                                                >
                                                                    <TableCell>
                                                                        <Chip
                                                                            icon={getStatusIcon(event.event)}
                                                                            label={titleCase(event.event)}
                                                                            color={getStatusColor(event.event)}
                                                                            size='small'
                                                                            sx={{
                                                                                fontWeight: 600,
                                                                                fontSize: '0.75rem',
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography
                                                                            variant='body2'
                                                                            sx={{ fontWeight: 500 }}
                                                                        >
                                                                            {formatDate(event.created)}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {event.reason &&
                                                                        event.reason !== 'N/A' &&
                                                                        event.reason !== 'Unavailable' ? (
                                                                            <Tooltip title={event.reason}>
                                                                                <Typography
                                                                                    variant='body2'
                                                                                    sx={{
                                                                                        maxWidth: 200,
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis',
                                                                                        whiteSpace: 'nowrap',
                                                                                        fontWeight: 500,
                                                                                    }}
                                                                                >
                                                                                    {event.reason}
                                                                                </Typography>
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <Typography
                                                                                variant='body2'
                                                                                color='text.secondary'
                                                                            >
                                                                                -
                                                                            </Typography>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {event.url && event.url !== 'N/A' ? (
                                                                            <Tooltip title={event.url}>
                                                                                <Typography
                                                                                    variant='body2'
                                                                                    component='a'
                                                                                    href={event.url}
                                                                                    target='_blank'
                                                                                    rel='noopener noreferrer'
                                                                                    sx={{
                                                                                        color: 'primary.main',
                                                                                        textDecoration: 'none',
                                                                                        maxWidth: 200,
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis',
                                                                                        whiteSpace: 'nowrap',
                                                                                        display: 'block',
                                                                                        fontWeight: 500,
                                                                                        '&:hover': {
                                                                                            textDecoration: 'underline',
                                                                                            color: 'primary.dark',
                                                                                        },
                                                                                    }}
                                                                                >
                                                                                    {event.url}
                                                                                </Typography>
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <Typography
                                                                                variant='body2'
                                                                                color='text.secondary'
                                                                            >
                                                                                -
                                                                            </Typography>
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Collapse>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Stack>
                )}
            </Box>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        color='primary'
                        size='large'
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}
        </Stack>
    );
}
